import { useLayoutEffect, useMemo, useRef } from 'react'
import { getScrollLeftToCenterColumn } from './_utils/getScrollLeftToCenterColumn'
import { getTableLayout } from './_utils/getTableLayout'
import { useIsScrolledToEdges } from './_utils/useIsScrolledToEdges'
import { useVisibleColumnRange } from './_utils/useVisibleColumnRange'
import { Bottom } from './Bottom/Bottom'
import { columnOverscan, MAGIC_NUMBER_TO_TAKE_OFF_WIDTH, maxColumnsRenderedInFull } from './consts'
import { GridTableScrollContext, GridTableVisibleColumnsContext } from './context'
import { RightSideShadow } from './RightSideShadow/RightSideShadow'
import { Top } from './Top/Top'
import type { GridTableProps } from './types'

export const GridTable = ({
  childrenUpper,
  columns,
  content,
  containerWidth,
  topLeftCell,
}: GridTableProps) => {
  const gridTableRef = useRef<HTMLDivElement>(null)

  const { tableWidth, widthForLabelColumn, widthForValueColumns } = useMemo(() => {
    return getTableLayout({ columns, content, containerWidth, topLeftCell })
  }, [content, columns, topLeftCell?.minWidth])

  const { allTheWayBottom, allTheWayLeft, allTheWayRight, allTheWayTop } = useIsScrolledToEdges({
    availableWidth: tableWidth,
    wrapperRef: gridTableRef?.current,
  })

  const offsetOfFirstColumn = widthForLabelColumn - MAGIC_NUMBER_TO_TAKE_OFF_WIDTH

  const visibleColumns = useVisibleColumnRange({
    columnCount: columns.length,
    columnWidth: widthForValueColumns,
    initialViewportWidth: containerWidth,
    offsetStart: offsetOfFirstColumn,
    overscan: columnOverscan,
    renderAllUpTo: maxColumnsRenderedInFull,
    scrollElementRef: gridTableRef,
  })

  useLayoutEffect(() => {
    const scrollElement = gridTableRef.current
    const columnIndex = columns.findIndex((column) => column.centerOnLoad)

    if (!scrollElement || columnIndex === -1) return

    const left = getScrollLeftToCenterColumn({
      columnIndex,
      columnWidth: widthForValueColumns,
      offsetStart: offsetOfFirstColumn,
      viewportWidth: scrollElement.clientWidth,
    })

    // animating across a long range renders every column on the way
    const isFarAway = Math.abs(left - scrollElement.scrollLeft) > scrollElement.clientWidth * 2

    scrollElement.scrollTo({ left, behavior: isFarAway ? 'auto' : 'smooth' })
  }, [columns])

  return (
    <div
      css={{
        display: 'flex',
        overflow: 'hidden',
        maxHeight: '100%',
        boxShadow: `inset 0px -50px 25px -25px rgba(180, 179, 187, 0${!allTheWayBottom ? '.7' : ''})`,
        transition: '200ms ease all',
        flexDirection: 'column',
      }}
    >
      <div css={{ padding: '10px 20px' }}>{childrenUpper}</div>
      <GridTableScrollContext.Provider value={{ allTheWayLeft, allTheWayRight }}>
        <div ref={gridTableRef} css={{ overflowX: 'auto', minWidth: `${containerWidth}px` }}>
          <GridTableVisibleColumnsContext.Provider value={visibleColumns}>
            <Top
              addShadow={!allTheWayTop}
              columns={columns}
              tableWidth={tableWidth}
              topLeftCell={topLeftCell}
              widthForLabelColumn={widthForLabelColumn}
              widthForValueColumns={widthForValueColumns}
            />
            <Bottom
              columns={columns}
              containerWidth={containerWidth}
              content={content}
              tableWidth={tableWidth}
              widthForLabelColumn={widthForLabelColumn}
              widthForValueColumns={widthForValueColumns}
            />
          </GridTableVisibleColumnsContext.Provider>
        </div>
        <RightSideShadow gridTableRef={gridTableRef} />
      </GridTableScrollContext.Provider>
    </div>
  )
}
